import { useEffect, useRef } from "react";

const dialogs = [];
export const isTopDialog = (element) => dialogs[dialogs.length - 1] === element;
const focusableSelector = 'button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';

function isVisible(element) {
  for (let node = element; node instanceof HTMLElement; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (node.hidden || node.inert || style.display === "none" || style.visibility === "hidden") return false;
  }
  return true;
}

// Includes linked listboxes and open calendars rendered outside the dialog.
// Only the top dialog owns Tab; nested SweetAlert dialogs manage their own focus.
export default function useDialogFocus() {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previous = document.activeElement;
    dialogs.push(dialog);
    if (!dialog.contains(document.activeElement)) {
      dialog.focus({ preventScroll: true });
    }
    const handleKey = (event) => {
      if (event.key !== "Tab" || !isTopDialog(dialog) || document.activeElement?.closest(".swal2-popup")) return;
      const portals = [...dialog.querySelectorAll("[aria-controls]")]
        .map((control) => document.getElementById(control.getAttribute("aria-controls")))
        .filter(Boolean);
      const roots = [dialog, ...portals, ...document.querySelectorAll(".flatpickr-calendar.open")];
      const targets = [...new Set(roots.flatMap((root) => [...root.querySelectorAll(focusableSelector)]))]
        .filter((element) => element.tabIndex >= 0 && isVisible(element));
      const first = targets[0];
      const last = targets[targets.length - 1];
      const active = document.activeElement;
      if (!first) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && (active === first || !targets.includes(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !targets.includes(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      const index = dialogs.indexOf(dialog);
      if (index !== -1) dialogs.splice(index, 1);
      if (previous?.isConnected && (!dialogs.length || dialogs[dialogs.length - 1].contains(previous))) previous.focus();
    };
  }, []);
  return ref;
}
